import { useWorkingTimeStore } from '@nsftx/seven-client-retail-vue-dm'
import {
  betslipService,
  bettingAcceptanceErrorParser,
  getBetPayoutAmount,
  useBetslip
} from '@nsftx/seven-client-retail-vue-sgp'
import type { Bet } from '@nsftx/seven-client-retail-vue-integrator'
import { logService } from '@nsftx/seven-client-retail-utils'
import type { PrintContextPayoutSlip } from '@nsftx/seven-client-retail-print'
import i18n from '@/plugins/translationsPlugin'
import { TicketPayoutHooksManager } from './ticketPayoutHooksManager'
import { useTicketCheckStore } from '@/stores/ticket-check/ticketCheckStore'
import { formatNumber, useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import TicketPayoutValidationError from './TicketPayoutValidationError'
import errorParser from '@/utils/errorParser'
import printService from '../print/services/printService'

const LOG_PREFIX = '[ticketPayoutService]'
const { t } = i18n.global

const initTicketPayout = () => {
  registerHooks()
}

const validateTicketPayout = () => TicketPayoutHooksManager.callHook('BeforeTicketPayout')

const ticketPayout = async (betslipId: string, bet: Bet) => {
  const { showNotification } = useNotificationsStore()
  const { closeTicketCheckDetailsModal } = useTicketCheckStore()
  const { betslip, betslipCurrency, betslipProductId } = useBetslip()

  const productId = betslipProductId.value
  const betIds = [bet.id]
  const payoutAmount = getBetPayoutAmount(bet)
  const formattedPayoutAmount = formatNumber(payoutAmount, { style: 'decimal' })

  try {
    logService.info(`${LOG_PREFIX} Sending ticket to Betslip API`, {
      code: 'ST_TICKET_PAYOUT_BETSLIP_REQUEST',
      details: {
        ticket_code: betslipId,
        product_displayid: productId,
        bets: betIds
      }
    })
    await betslipService.payoutTicket(betslipId, betIds)
    logService.info(`${LOG_PREFIX} Ticket paid out via Betslip API`, {
      code: 'ST_TICKET_PAYOUT_BETSLIP_SUCCESS',
      details: {
        ticket_code: betslipId,
        product_displayid: productId
      }
    })

    const printAddJobOptions: PrintContextPayoutSlip = {
      meta: {
        productDisplayId: productId?.toString() || '',
        requestId: betslipId
      },
      model: {
        slip: betslip.value!
      }
    }

    await printService
      .getPrintService()
      .performPrint(`games${productId}`, 'betslip_payout', printAddJobOptions)

    closeTicketCheckDetailsModal()
    showNotification({
      variant: 'success',
      text: [
        t('ticket_payout_success', {
          amount: formattedPayoutAmount,
          currency: betslipCurrency.value
        })
      ]
    })
  } catch (error) {
    logService.error(`${LOG_PREFIX} Failed to payout ticket`, {
      code: 'ST_TICKET_PAYOUT_ERROR',
      details: {
        ticket_code: betslipId,
        product_displayid: productId,
        bets: betIds
      },
      ...bettingAcceptanceErrorParser.parseUpstream(error)
    })

    showNotification({
      variant: 'error',
      text: [
        t('ticket_payout_error', {
          amount: formattedPayoutAmount,
          currency: betslipCurrency.value
        })
      ]
    })
  }
}

const handleTicketPayoutValidationError = (error: unknown) => {
  const { showNotification } = useNotificationsStore()
  const errorMessage =
    error instanceof TicketPayoutValidationError ? error.message : t('common_error')

  logService.error(`${LOG_PREFIX} Ticket payout validation has failed`, {
    code: 'ST_TICKET_PAYOUT_VALIDATION_ERROR',
    ...errorParser.parseUpstream(error)
  })
  showNotification({
    variant: 'error',
    text: [errorMessage]
  })
}

const handleTicketPayout = async (betslipId: string, bet: Bet) => {
  try {
    await validateTicketPayout()
    await ticketPayout(betslipId, bet)
  } catch (error) {
    handleTicketPayoutValidationError(error)
  }
}

const registerHooks = () => {
  TicketPayoutHooksManager.hook('BeforeTicketPayout', () => {
    const workingTimeStore = useWorkingTimeStore()
    const { isValid, nextValidTime } = workingTimeStore.isWorkingTimeValid()

    if (!isValid) {
      const errorMessage = t('working_time_over', {
        time: nextValidTime || t('common_unknown')
      })

      return Promise.reject(
        new TicketPayoutValidationError(
          errorMessage,
          'ST_TICKET_PAYOUT_VALIDATION_WORKING_TIME_ERROR'
        )
      )
    }

    return Promise.resolve()
  })
}

export { initTicketPayout, handleTicketPayout }
